// Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
// See LICENSE in the project root for license information.
import graphStyles from '../styles/graphView.module.css';
import { pruneGraphOperations } from './graphFiltering';
export const GRAPH_NODE_WIDTH = 28;
export const GRAPH_NODE_HEIGHT = 28;
export const GRAPH_COL_WIDTH = 46;
export const GRAPH_NODE_GAP = 10;
export const GRAPH_LEVEL_GAP = 70;
export const GRAPH_BASE_X = 16;
export const GRAPH_BASE_Y = 16;
export const graphState = {
    nodePositions: new Map(),
    nodeStatus: new Map(),
    nodeElements: new Map(),
    edgeElements: []
};
export function createGraphViewController(options) {
    let graphNeedsFullRender = true;
    function getStatusColors() {
        var _a, _b, _c;
        const cs = getComputedStyle(document.documentElement);
        return {
            Ready: cs.getPropertyValue('--status-ready').trim(),
            Waiting: cs.getPropertyValue('--status-waiting').trim(),
            Queued: cs.getPropertyValue('--status-queued').trim(),
            Executing: ((_a = cs.getPropertyValue('--status-executing')) === null || _a === void 0 ? void 0 : _a.trim()) || cs.getPropertyValue('--warn').trim(),
            Success: ((_b = cs.getPropertyValue('--status-success')) === null || _b === void 0 ? void 0 : _b.trim()) || cs.getPropertyValue('--success').trim(),
            SuccessWithWarning: cs.getPropertyValue('--status-success-warning').trim(),
            Skipped: cs.getPropertyValue('--status-skipped').trim(),
            FromCache: cs.getPropertyValue('--status-from-cache').trim(),
            Failure: ((_c = cs.getPropertyValue('--status-failure')) === null || _c === void 0 ? void 0 : _c.trim()) || cs.getPropertyValue('--danger').trim(),
            Blocked: cs.getPropertyValue('--status-blocked').trim(),
            NoOp: cs.getPropertyValue('--status-noop').trim(),
            Aborted: cs.getPropertyValue('--status-aborted').trim()
        };
    }
    let statusColors = getStatusColors();
    const mo = new MutationObserver(() => {
        statusColors = getStatusColors();
    });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
    const updateStatusColors = () => {
        statusColors = getStatusColors();
    };
    function computeLevels(filteredOps) {
        const indegree = new Map();
        const deps = new Map();
        filteredOps.forEach((op) => {
            deps.set(op.name, op.dependencies || []);
            indegree.set(op.name, (op.dependencies || []).length);
        });
        const queue = [];
        indegree.forEach((v, k) => {
            if (v === 0)
                queue.push(k);
        });
        const level = new Map();
        queue.forEach((k) => level.set(k, 0));
        while (queue.length) {
            const cur = queue.shift();
            if (!cur)
                continue;
            const curLevel = level.get(cur) || 0;
            filteredOps.forEach((op) => {
                if ((op.dependencies || []).includes(cur)) {
                    indegree.set(op.name, (indegree.get(op.name) || 0) - 1);
                    if (!level.has(op.name) || (level.get(op.name) || 0) < curLevel + 1) {
                        level.set(op.name, curLevel + 1);
                    }
                    if ((indegree.get(op.name) || 0) === 0)
                        queue.push(op.name);
                }
            });
        }
        return level;
    }
    function computeGraphOperations() {
        const filteredOutNames = options.getFilteredOutNames();
        const searchFilteredOutNames = options.getSearchFilteredOutNames();
        const visibleOperations = [];
        for (const op of options.getOperations().values()) {
            if (filteredOutNames.has(op.name))
                continue;
            if (searchFilteredOutNames.has(op.name))
                continue;
            visibleOperations.push(op);
        }
        return pruneGraphOperations(visibleOperations);
    }
    function dimColor(hex, amount = 0.55) {
        if (!hex || !/^#?[0-9a-fA-F]{6}$/.test(hex))
            return hex || '#4b5563';
        if (hex[0] === '#')
            hex = hex.slice(1);
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        const br = 30;
        const bg = 41;
        const bb = 59;
        const nr = Math.round(r * (1 - amount) + br * amount);
        const ng = Math.round(g * (1 - amount) + bg * amount);
        const nb = Math.round(b * (1 - amount) + bb * amount);
        return ('#' +
            nr.toString(16).padStart(2, '0') +
            ng.toString(16).padStart(2, '0') +
            nb.toString(16).padStart(2, '0'));
    }
    function buildGraph() {
        graphState.nodePositions.clear();
        graphState.nodeStatus.clear();
        graphState.nodeElements.forEach((el) => el.remove());
        graphState.nodeElements.clear();
        graphState.edgeElements.forEach((e) => e.path.remove());
        graphState.edgeElements.length = 0;
        options.edgesSvg.innerHTML =
            '<defs>' +
                Object.entries(statusColors)
                    .map(([status, color]) => `<marker id="arrowhead-${status}" markerWidth="10" markerHeight="7" refX="8" refY="3.5" orient="auto"><polygon points="0 0, 10 3.5, 0 7" fill="${color}" /></marker>`)
                    .join('') +
                '</defs>';
        const filteredOpsArr = computeGraphOperations();
        const level = computeLevels(filteredOpsArr);
        const groups = {};
        level.forEach((value, name) => (groups[value] || (groups[value] = [])).push(name));
        const sortedLevels = Object.keys(groups)
            .map(Number)
            .sort((a, b) => a - b);
        const maxLevel = sortedLevels.length ? Math.max(...sortedLevels) : 0;
        const dependentsMap = new Map();
        filteredOpsArr.forEach((op) => {
            (op.dependencies || []).forEach((dependencyName) => {
                if (!options.getOperations().has(dependencyName))
                    return;
                let setForDependency = dependentsMap.get(dependencyName);
                if (!setForDependency) {
                    setForDependency = new Set();
                    dependentsMap.set(dependencyName, setForDependency);
                }
                setForDependency.add(op.name);
            });
        });
        const byNameFiltered = new Map(filteredOpsArr.map((op) => [op.name, op]));
        const memoCpl = new Map();
        function criticalPathLen(name) {
            const cached = memoCpl.get(name);
            if (cached !== undefined)
                return cached;
            const op = byNameFiltered.get(name);
            if (!op) {
                memoCpl.set(name, 0);
                return 0;
            }
            const deps = Array.from(dependentsMap.get(name) || new Set());
            if (!deps.length) {
                memoCpl.set(name, 0);
                return 0;
            }
            let best = 0;
            for (const dependencyName of deps) {
                best = Math.max(best, 1 + criticalPathLen(dependencyName));
            }
            memoCpl.set(name, best);
            return best;
        }
        sortedLevels.forEach((levelValue) => {
            const nodes = groups[levelValue] || [];
            nodes.sort((a, b) => {
                const cplA = criticalPathLen(a);
                const cplB = criticalPathLen(b);
                if (cplA !== cplB)
                    return cplB - cplA;
                const consA = (dependentsMap.get(a) || new Set()).size;
                const consB = (dependentsMap.get(b) || new Set()).size;
                if (consA !== consB)
                    return consB - consA;
                return a.localeCompare(b);
            });
            const levelIndexFromTop = maxLevel - levelValue;
            nodes.forEach((name, index) => {
                const op = options.getOperations().get(name);
                if (!op)
                    return;
                const x = GRAPH_BASE_X + index * (GRAPH_COL_WIDTH + GRAPH_NODE_GAP);
                const y = GRAPH_BASE_Y + levelIndexFromTop * GRAPH_LEVEL_GAP;
                const button = document.createElement('button');
                button.type = 'button';
                button.setAttribute('aria-label', name);
                button.className = graphStyles.opNode;
                button.dataset.name = name;
                button.style.transform = `translate(${x}px, ${y}px)`;
                const emojiSpan = document.createElement('span');
                emojiSpan.className = graphStyles.emoji;
                emojiSpan.textContent = options.getStatusEmoji(options.getComputeDisplayStatus(op));
                button.appendChild(emojiSpan);
                const enabledSup = document.createElement('span');
                enabledSup.className = graphStyles.enabledIndicator;
                enabledSup.textContent = '';
                button.appendChild(enabledSup);
                button.addEventListener('click', (e) => {
                    e.stopPropagation();
                    if (e.metaKey || e.ctrlKey)
                        options.toggleSelect(name);
                    else
                        options.singleSelect(name);
                });
                options.graphEl.appendChild(button);
                graphState.nodePositions.set(name, { x, y });
                graphState.nodeElements.set(name, button);
            });
        });
        const byName = new Map(filteredOpsArr.map((op) => [op.name, op]));
        const memoReach = new Map();
        function getReachable(name) {
            const cached = memoReach.get(name);
            if (cached)
                return cached;
            const op = byName.get(name);
            const visited = new Set();
            if (op) {
                const stack = [...(op.dependencies || [])];
                while (stack.length) {
                    const dependencyName = stack.pop();
                    if (!dependencyName)
                        continue;
                    if (visited.has(dependencyName))
                        continue;
                    visited.add(dependencyName);
                    const dependencyOp = byName.get(dependencyName);
                    if (dependencyOp)
                        stack.push(...(dependencyOp.dependencies || []));
                }
            }
            memoReach.set(name, visited);
            return visited;
        }
        const edgeRecords = [];
        for (const op of filteredOpsArr) {
            const deps = op.dependencies || [];
            for (const depName of deps) {
                if (!byName.has(depName))
                    continue;
                let redundant = false;
                for (const intermediate of deps) {
                    if (intermediate === depName)
                        continue;
                    if (!byName.has(intermediate))
                        continue;
                    if (getReachable(intermediate).has(depName)) {
                        redundant = true;
                        break;
                    }
                }
                if (redundant)
                    continue;
                const fromPos = graphState.nodePositions.get(op.name);
                const toPos = graphState.nodePositions.get(depName);
                if (!fromPos || !toPos)
                    continue;
                const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                edgeRecords.push({ path, from: op.name, to: depName });
                options.edgesSvg.appendChild(path);
            }
        }
        graphState.edgeElements = edgeRecords;
        updateGraph();
        if (graphState.nodePositions.size) {
            const maxX = Math.max(...Array.from(graphState.nodePositions.values()).map((p) => p.x)) + GRAPH_NODE_WIDTH + 40;
            const maxY = Math.max(...Array.from(graphState.nodePositions.values()).map((p) => p.y)) +
                GRAPH_LEVEL_GAP +
                GRAPH_NODE_HEIGHT;
            options.edgesSvg.setAttribute('width', String(maxX));
            options.edgesSvg.setAttribute('height', String(maxY));
        }
    }
    function updateGraph() {
        updateStatusColors();
        for (const [name, div] of graphState.nodeElements.entries()) {
            const op = options.getOperations().get(name);
            if (!op)
                continue;
            const displayStatus = options.getComputeDisplayStatus(op);
            const prevStatus = graphState.nodeStatus.get(name);
            const state = options.getExecutionStates().get(name);
            const runInThisIteration = state
                ? state.runInThisIteration
                : op.runInThisIteration;
            const notRunning = runInThisIteration === false || !!op.noop;
            const queuedState = options.getQueuedStates().get(name);
            const isQueuedNext = !!(queuedState && queuedState.runInThisIteration === true);
            const isFilteredOut = options.getFilteredOutNames().has(name);
            const isSearchFiltered = options.getSearchFilteredOutNames().has(name);
            const emojiSpan = div.getElementsByClassName(graphStyles.emoji)[0];
            if (emojiSpan && (prevStatus !== displayStatus || !emojiSpan.textContent)) {
                emojiSpan.textContent = options.getStatusEmoji(displayStatus);
            }
            const enabledSpan = div.getElementsByClassName(graphStyles.enabledIndicator)[0];
            if (enabledSpan) {
                let indicator = '';
                if (op.noop) {
                    indicator = '⚪';
                    enabledSpan.title = 'No-op operation';
                }
                else {
                    switch (op.enabled) {
                        case 'never':
                            indicator = '🔴';
                            enabledSpan.title = 'Disabled';
                            break;
                        case 'ignore-dependency-changes':
                            indicator = '🟡';
                            enabledSpan.title = 'Ignores dependency changes';
                            break;
                        case 'affected':
                        default:
                            indicator = '🟢';
                            enabledSpan.title = 'Enabled';
                            break;
                    }
                }
                if (enabledSpan.textContent !== indicator)
                    enabledSpan.textContent = indicator;
            }
            let baseColor = statusColors[displayStatus] || '#4b5563';
            if (isSearchFiltered)
                baseColor = dimColor(baseColor, 0.72);
            else if (isFilteredOut)
                baseColor = dimColor(baseColor, 0.6);
            else if (notRunning)
                baseColor = dimColor(baseColor, 0.35);
            div.style.borderColor = baseColor;
            const isSelected = options.getSelection().has(name);
            div.setAttribute('aria-pressed', String(isSelected));
            if (isSelected)
                div.classList.add(graphStyles.selected);
            else
                div.classList.remove(graphStyles.selected);
            let activeSpan = div.getElementsByClassName(graphStyles.activeIndicator)[0];
            if (op.isActive) {
                if (!activeSpan) {
                    activeSpan = document.createElement('span');
                    activeSpan.className = graphStyles.activeIndicator;
                    activeSpan.textContent = '⚡';
                    div.appendChild(activeSpan);
                }
                activeSpan.title = 'Active (in-memory state)';
            }
            else if (activeSpan) {
                activeSpan.remove();
            }
            let pendingSpan = div.getElementsByClassName(graphStyles.pendingIndicator)[0];
            if (isQueuedNext) {
                if (!pendingSpan) {
                    pendingSpan = document.createElement('span');
                    pendingSpan.className = graphStyles.pendingIndicator;
                    pendingSpan.textContent = '🕒';
                    div.appendChild(pendingSpan);
                }
                pendingSpan.title = 'Pending changes (iteration queued)';
            }
            else if (pendingSpan) {
                pendingSpan.remove();
            }
            div.classList.remove(graphStyles.notRunning, graphStyles.filteredOut, graphStyles.filteredOutSearch, graphStyles.dashed, graphStyles.dotted);
            if (isSearchFiltered) {
                div.classList.add(graphStyles.filteredOutSearch);
            }
            else if (isFilteredOut) {
                div.classList.add(graphStyles.filteredOut);
            }
            else if (notRunning) {
                div.classList.add(graphStyles.notRunning);
            }
            div.title = `${op.name}\nLast Result: ${(options.getLastExecutionResults().get(name) || {}).status || displayStatus}\n${options.getOverallStatusText(displayStatus)}${op.isActive ? '\nHas in-memory state' : ''}`;
            graphState.nodeStatus.set(name, displayStatus);
        }
        for (const rec of graphState.edgeElements) {
            const fromPos = graphState.nodePositions.get(rec.from);
            const toPos = graphState.nodePositions.get(rec.to);
            if (!fromPos || !toPos)
                continue;
            const startX = fromPos.x + GRAPH_NODE_WIDTH / 2;
            const startY = fromPos.y + GRAPH_NODE_HEIGHT;
            const endX = toPos.x + GRAPH_NODE_WIDTH / 2;
            const endY = toPos.y;
            const rowsApart = Math.max(1, Math.round((toPos.y - fromPos.y) / GRAPH_LEVEL_GAP));
            const colStep = GRAPH_COL_WIDTH + GRAPH_NODE_GAP;
            function quadratic(sx, sy, ex, ey) {
                const mx = (sx + ex) / 2;
                const my = (sy + ey) / 2;
                const baseOffset = (ey - sy) / 4;
                return `Q ${sx} ${sy + baseOffset} ${mx} ${my} ${ex} ${my + baseOffset} ${ex} ${ey}`;
            }
            let d = '';
            if (startX === endX && rowsApart === 1) {
                d = `M ${startX} ${startY} L ${endX} ${endY}`;
            }
            else if (rowsApart === 1) {
                d = `M ${startX} ${startY} ` + quadratic(startX, startY, endX, endY);
            }
            else {
                const dir = Math.sign(endX - startX) || 1;
                const halfColShift = colStep / 2;
                const candidateX = startX + (endX - startX) * 0.5;
                const deltaCols = Math.max(1, Math.round(Math.abs(endX - startX) / colStep));
                let intermediateX = candidateX;
                let tooClose = false;
                for (let k = 0; k <= deltaCols; k++) {
                    const center = startX + dir * k * colStep;
                    if (Math.abs(candidateX - center) < GRAPH_NODE_WIDTH / 2 + 2) {
                        tooClose = true;
                        break;
                    }
                }
                if (tooClose)
                    intermediateX = candidateX + dir * halfColShift;
                const firstTargetY = fromPos.y + GRAPH_LEVEL_GAP;
                const bottomOfRowAboveDest = toPos.y - GRAPH_LEVEL_GAP + GRAPH_NODE_HEIGHT;
                const midY1 = firstTargetY;
                const midY2 = bottomOfRowAboveDest;
                d = `M ${startX} ${startY} ` + quadratic(startX, startY, intermediateX, midY1);
                d += ` L ${intermediateX} ${midY2}`;
                d += ' ' + quadratic(intermediateX, midY2, endX, endY);
            }
            rec.path.setAttribute('d', d);
            const depStatus = graphState.nodeStatus.get(rec.to) || 'Ready';
            rec.path.setAttribute('stroke', statusColors[depStatus] || '#4b5563');
            rec.path.setAttribute('class', graphStyles.edge);
            rec.path.setAttribute('marker-end', `url(#arrowhead-${depStatus})`);
            const fromOp = options.getOperations().get(rec.from);
            if (options.getSelection().has(rec.to) && options.getSelection().has(rec.from))
                rec.path.classList.add(graphStyles.highlight);
            else
                rec.path.classList.remove(graphStyles.highlight);
            rec.path.classList.remove(graphStyles.dashed, graphStyles.dotted, graphStyles.filteredOut, graphStyles.notRunning);
            rec.path.classList.remove(graphStyles.filteredOutSearch);
            const fromState = fromOp
                ? options.getExecutionStates().get(rec.from)
                : undefined;
            const fromRunInThisIteration = fromState
                ? fromState.runInThisIteration
                : fromOp === null || fromOp === void 0 ? void 0 : fromOp.runInThisIteration;
            const fromNotRunning = !!(fromOp && (fromRunInThisIteration === false || fromOp.noop));
            const edgeStatusFiltered = options.getFilteredOutNames().has(rec.from) || options.getFilteredOutNames().has(rec.to);
            const edgeSearchFiltered = options.getSearchFilteredOutNames().has(rec.from) || options.getSearchFilteredOutNames().has(rec.to);
            if (edgeSearchFiltered || edgeStatusFiltered || fromNotRunning) {
                let strokeColor = statusColors[depStatus] || '#4b5563';
                if (edgeSearchFiltered) {
                    strokeColor = dimColor(strokeColor, 0.78);
                    rec.path.style.opacity = '0.22';
                }
                else if (edgeStatusFiltered) {
                    strokeColor = dimColor(strokeColor, 0.65);
                    rec.path.style.opacity = '0.3';
                }
                else if (fromNotRunning) {
                    strokeColor = dimColor(strokeColor, 0.4);
                    rec.path.style.opacity = '0.42';
                }
                rec.path.setAttribute('stroke', strokeColor);
                rec.path.setAttribute('marker-end', `url(#arrowhead-${depStatus})`);
            }
            else {
                rec.path.style.opacity = '';
                const semImportant = depStatus === 'Executing' || depStatus === 'Failure';
                if (!semImportant && !rec.path.classList.contains(graphStyles.highlight)) {
                    rec.path.classList.add(graphStyles.dim);
                }
                else {
                    rec.path.classList.remove(graphStyles.dim);
                }
            }
        }
        options.renderPhaseLegend();
    }
    function markGraphDirty() {
        graphNeedsFullRender = true;
    }
    function ensureGraph() {
        if (graphNeedsFullRender) {
            buildGraph();
            graphNeedsFullRender = false;
        }
        else {
            updateGraph();
        }
    }
    return {
        markGraphDirty,
        ensureGraph,
        updateGraph
    };
}
//# sourceMappingURL=graphView.js.map